import type React from 'react'

import type {
  FormInstance,
  NineField,
  NineFieldValue,
  NineFormComponent,
  NineFormNodeInfo,
  NineNodeBase,
  NineUIProps,
  ReactObj,
} from './types'
import { DetailHandler, EditFormHandler } from './handlers'
import { getUIAdapter } from './state'

type GenerateOptions = Record<string, unknown> & {
  t?: (key: string) => string
  uiNodeProps?: NineUIProps
}

export function generateEditUI(
  fields: Record<string, NineFieldValue> | undefined,
  options: GenerateOptions,
  values: Record<string, unknown> | null | undefined,
  reactObj: ReactObj | null | undefined,
): EditFormHandler {
  const compList: Record<string, NineFormComponent> = {}
  const nodeInfo: NineFormNodeInfo = {
    list: [],
    map: {},
    memTableMap: {},
    options,
  }

  Object.entries(fields || {}).forEach(([key, value0]) => {
    const value00 = value0 as NineNodeBase | NineField
    if (value00 === null || (value00.nineType !== 'field' && value00.nineType !== 'node')) {
      return
    }

    const value = value00
    const { uiProps, metaProps } = value as NineNodeBase

    if (value.nineType === 'node') {
      const subValues = values != null ? (values[key] as Record<string, unknown>) : null

      const opts: GenerateOptions = options != null ? { ...options } : {}

      if ((value as NineNodeBase).uiProps != null) {
        opts.uiNodeProps = (value as NineNodeBase).uiProps
      } else {
        delete opts.uiNodeProps
      }

      const compObj = generateEditUI((value as NineNodeBase).fields, opts, subValues, reactObj)
      const nodeData = {
        uiProps,
        node: compObj,
        isTable: (value as NineNodeBase).isNodeConnection === true,
        key,
      }
      nodeInfo.map[key] = nodeData
      nodeInfo.list.push(nodeData)
      return
    }

    if (uiProps == null) return

    if (uiProps.modeFunc != null) {
      uiProps.mode = uiProps.modeFunc(key, value, uiProps, values ?? null, reactObj as ReactObj)
    }

    if (
      uiProps.mode !== 'all' &&
      uiProps.mode !== 'write' &&
      uiProps.mode !== 'read' &&
      uiProps.mode !== 'readonly'
    ) {
      return
    }

    if (options.uiNodeProps != null && options.uiNodeProps.i18nextKey != null) {
      if (uiProps.labelKey == null && uiProps.label == null) {
        uiProps.labelKey = `${options.uiNodeProps.i18nextKey}.field.${key}`
      }
    }

    let label = options.t && uiProps.labelKey ? options.t(uiProps.labelKey) : uiProps.label
    let editLabel =
      options.t && uiProps.editLabelKey ? options.t(uiProps.editLabelKey) : uiProps.editLabel
    let tableLabel =
      options.t && uiProps.tableLabelKey ? options.t(uiProps.tableLabelKey) : uiProps.tableLabel
    let desc = options.t && uiProps.descKey ? options.t(uiProps.descKey) : uiProps.desc
    let afterText =
      options.t && uiProps.afterTextKey ? options.t(uiProps.afterTextKey) : uiProps.afterText

    if (uiProps.getText != null) {
      label = uiProps.getText('label', label as string, uiProps, metaProps || {})
      editLabel = uiProps.getText('edit', editLabel as string, uiProps, metaProps || {})
      tableLabel = uiProps.getText('table', tableLabel as string, uiProps, metaProps || {})
      desc = uiProps.getText('desc', desc as string, uiProps, metaProps || {})
      afterText = uiProps.getText('afterText', afterText as string, uiProps, metaProps || {})
    }

    const name = uiProps.name ? uiProps.name : key
    const type = uiProps.type ? uiProps.type.toLowerCase() : 'text'
    let val: unknown = values != null ? values[name] : ''

    const adapter = getUIAdapter()
    if (adapter.valueSetFilter != null) {
      val = adapter.valueSetFilter(true, uiProps.type as string, val as string)
    }

    const compInfo: Record<string, unknown> = {
      name: name as string,
      type,
      desc,
      afterText,
      label,
      editLabel: editLabel || label,
      tableLabel: tableLabel || label,
      origin: uiProps,
      config: uiProps.config,
      options,
      value: val,
      initValue: undefined as unknown,
    }

    if (values != null) {
      // gql로 읽어온 결과로 초기값 설정.
      let initVal = values[compInfo.name as string]
      if (adapter.valueSetFilter != null) {
        initVal = adapter.valueSetFilter(true, uiProps.type as string, initVal as string)
      }
      compInfo.initValue = initVal
    }

    if (uiProps.config != null && uiProps.config.rules != null) {
      uiProps.config.rules.forEach((item) => {
        if (options.t && (item as Record<string, unknown>).messageKey) {
          ;(item as Record<string, unknown>).message = options.t(
            (item as Record<string, unknown>).messageKey as string,
          )
        }
        const validatorFunc = (item as Record<string, unknown>).validatorFunc
        if (validatorFunc) {
          ;(item as Record<string, unknown>).validator = (
            validatorFunc as (...args: unknown[]) => unknown
          )(options.t)
        }
      })
    }

    const comp = adapter.createEditElem(compInfo, reactObj, val, values) as
      | React.ReactElement
      | string
      | null

    if (comp != null) {
      compList[key] = {
        info: compInfo,
        component: comp as React.ReactElement,
        owner: reactObj as ReactObj,
      }
    }
  })

  return new EditFormHandler(compList, nodeInfo)
}

export function generateDetailUIByKey(
  key: string,
  value: NineNodeBase | NineField | null,
  values: Record<string, unknown> | null | undefined,
  options: GenerateOptions,
  reactObj: ReactObj | null | undefined,
  nodeInfo: NineFormNodeInfo | null,
): NineFormComponent | null {
  if (value === null || (value.nineType !== 'field' && value.nineType !== 'node')) {
    return null
  }

  const { uiProps } = value as NineNodeBase

  if (value.nineType === 'node') {
    const subValues = values != null ? (values[key] as Record<string, unknown>) : null

    const opts: GenerateOptions = options != null ? { ...options } : {}

    if (uiProps != null) {
      opts.uiNodeProps = uiProps
    } else {
      delete opts.uiNodeProps
    }

    const compObj = generateDetailUI((value as NineNodeBase).fields, opts, subValues, reactObj)
    const nodeData = {
      uiProps,
      node: compObj,
      isTable: (value as NineNodeBase).isNodeConnection === true,
      key,
    }

    if (nodeInfo != null) {
      nodeInfo.map[key] = nodeData
      nodeInfo.list.push(nodeData)
    }
    return null
  }

  if (uiProps == null) return null

  if (uiProps.modeFunc != null) {
    uiProps.mode = uiProps.modeFunc(key, value, uiProps, values ?? null, reactObj as ReactObj)
  }

  if (
    (uiProps.mode !== 'all' && uiProps.mode !== 'read' && uiProps.mode !== 'readonly') ||
    uiProps.type === 'hidden'
  ) {
    return null
  }

  if (options.uiNodeProps != null && options.uiNodeProps.i18nextKey != null) {
    if (uiProps.labelKey == null && uiProps.label == null) {
      uiProps.labelKey = `${options.uiNodeProps.i18nextKey}.field.${key}`
    }
  }

  const name = uiProps.name ? uiProps.name : key
  let val: unknown = values != null ? values[name] : ''

  const adapter = getUIAdapter()
  if (adapter.valueSetFilter != null) {
    val = adapter.valueSetFilter(false, uiProps.type as string, val as string)
  }

  const compInfo: Record<string, unknown> = {
    name,
    type: uiProps.type ? uiProps.type.toLowerCase() : 'text',
    desc: options.t && uiProps.descKey ? options.t(uiProps.descKey) : uiProps.desc,
    afterText:
      options.t && uiProps.afterTextKey ? options.t(uiProps.afterTextKey) : uiProps.afterText,
    label: options.t && uiProps.labelKey ? options.t(uiProps.labelKey) : uiProps.label,
    origin: uiProps,
    config: uiProps.config,
    options,
    value: val,
  }

  const comp = adapter.createDetailElem(compInfo, reactObj, val, values) as
    | React.ReactElement
    | string
    | null

  if (comp != null) {
    return {
      info: compInfo,
      detail: comp as React.ReactElement,
      owner: reactObj as ReactObj,
    }
  }
  return null
}

export function generateDetailUI(
  fields: Record<string, NineFieldValue> | undefined,
  options: GenerateOptions,
  values: Record<string, unknown> | null | undefined,
  reactObj: ReactObj | null | undefined,
): DetailHandler {
  const compList: Record<string, NineFormComponent> = {}
  const nodeInfo: NineFormNodeInfo = {
    list: [],
    map: {},
    memTableMap: {},
    options,
  }

  Object.entries(fields || {}).forEach(([key, value]) => {
    const compObj = generateDetailUIByKey(
      key,
      value as NineNodeBase | NineField | null,
      values,
      options,
      reactObj,
      nodeInfo,
    )

    if (compObj != null) {
      compList[key] = compObj
    }
  })

  return new DetailHandler(compList, nodeInfo)
}

/**
 * 쿼리 fields 선언(NineNodeBase.fields) 기반으로 현재 form 의 값을 추출.
 * ResultObj 가 관리하는 것과 별개로, Ngql.getFormFields(nodeField, form) 로 직접 호출된다.
 */
export function getFormFields(
  fields: Record<string, NineFieldValue>,
  formRef: React.RefObject<FormInstance>,
): Record<string, unknown> {
  const fieldsValue = formRef.current!.getFieldsValue()

  const props: Record<string, unknown> = {}

  Object.entries(fields).forEach(([key, value0]) => {
    if (value0 === null || (value0 as NineNodeBase).nineType !== 'field') {
      return
    }

    const value = value0 as NineNodeBase

    const { uiProps } = value

    if (uiProps != null && (uiProps.mode == null || uiProps.mode === 'none')) {
      return
    }

    let required = false

    if (uiProps != null && uiProps.config && uiProps.config.rules) {
      required = uiProps.config.rules.some((current) => {
        return current && current.required !== false
      })
    }

    if (required === true) {
      props[key] = fieldsValue[key]
    } else if (
      fieldsValue[key] != null &&
      uiProps != null &&
      uiProps.mode !== 'read' &&
      uiProps.mode !== 'readonly'
    ) {
      props[key] = fieldsValue[key]
    }
  })

  return props
}
